/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 *
 * Added for @bfx/provide-webworker multiworker project
 *
 */

import { Inject, EventEmitter, Injectable, Type } from "@angular/core";

import { MessageBus } from "../shared/message_bus";
import { Serializer, PRIMITIVE } from "../shared/serializer";

import { REGISTRY_CHANNEL, ON_WEB_WORKER, DEBUG } from "./api";
import {
  ClientMessageBroker,
  ClientMessageBroker_,
  FnArg,
  FnTransferableArg,
  UiArguments,
} from "./client_message_broker";
import { ServiceMessageBroker, ServiceMessageBroker_ } from "./service_message_broker";
import { PromiseSet } from "./promise_set";
import { ServiceMessageBrokerRegistry } from "./service_message_broker_registry";
import { REGISTER_SERVICE, REGISTER_CLIENT } from "./registry";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class ServiceMessageBrokerRegistryPrimary extends ServiceMessageBrokerRegistry {
  _registry: Map<string, PromiseSet<MessagePort>> = new Map();
  _registryClientResolve: Map<string, Function> = new Map();

  // TODO Add Timeouts if server doesn't register
  // _registryClientReject: Map<string, Function> = new Map();

  _broker: ServiceMessageBroker = null;
  private isDebug: boolean;

  constructor(
    private _messageBus: MessageBus,
    private _serializer: Serializer,
    @Inject(DEBUG) isDebug,
  ) {
    super();
    this.isDebug = isDebug;
    this._broker = this.createRegistryMessageBroker();
    this._broker.registerMethod(
      REGISTER_SERVICE,
      [PRIMITIVE, PRIMITIVE],
      this.registerServiceMessageBroker.bind(this),
      null,
    );
    this._broker.registerMethod(
      REGISTER_CLIENT,
      [PRIMITIVE, PRIMITIVE],
      this.registerClientMessageBroker.bind(this),
      null,
    );
  }

  private createRegistryMessageBroker(): ServiceMessageBroker {
    let channel = REGISTRY_CHANNEL;
    let runInZone = true;
    let hasTransfers = true;

    this._messageBus.initChannel(channel, runInZone, hasTransfers);

    let messageBroker = new ServiceMessageBroker_(this._messageBus, this._serializer, channel);
    messageBroker.isDebug = this.isDebug;

    return messageBroker;
  }

  registerServiceMessageBroker(channel: string, serviceRegistryPort: MessagePort): void {
    if (!this._registry.has(channel)) {
      this._registry.set(channel, new PromiseSet<MessagePort>());
    }

    if (this._registry.get(channel).isResolved()) {
      let reasonMsg = `Service Channel ${channel} already registered. These must be application unique.`;
      throw new Error(reasonMsg);
    }

    serviceRegistryPort.addEventListener("message", this.registrationAckListener.bind(this));
    serviceRegistryPort.start();

    this._registry.get(channel).resolve(serviceRegistryPort);

    if (this.isDebug) {
      logger.out.info(
        `registerServiceMessageBroker: [PRIMARY] ${channel} registered : ${serviceRegistryPort}`,
      );
    }
  }

  private registrationAckListener(event: MessageEvent) {
    if (
      event.data &&
      event.data.channel &&
      "ackClientRegistration" === event.data.type &&
      this.isDebug
    ) {
      // not really used outside of debug

      logger.out.info(`registrationAckListener ${event.data.channel} registered`);
    }
  }

  registerClientMessageBroker(channel: string, clientPort: MessagePort): void {
    if (!this._registry.has(channel)) {
      this._registry.set(channel, new PromiseSet<any>());
    }

    this._registry
      .get(channel)
      .get<MessagePort>()
      .then(
        (serviceToRegistryPort: MessagePort) => {
          if (this.isDebug) {
            logger.out.info(
              `registerClientPrimaryMessageBroker: sending port ${channel} registered : ${clientPort}`,
            );
          }

          serviceToRegistryPort.postMessage({ port: clientPort }, [clientPort]);
        },
        (reason) => {
          logger.out.info(
            `registerClientPrimaryMessageBroker: sending port ${channel} registered : ${clientPort}`,
          );
        },
      );
  }
}
